import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  addDoc, 
  deleteDoc,
  Timestamp,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from './firebase.config';

export interface Interview {
  id?: string;
  userId: string;
  userName: string;
  userEmail: string;
  companyName: string;
  position: string;
  interviewDate: Timestamp;
  interviewTime: string;
  interviewLocation?: string;
  interviewType: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactRole?: string;
  notes?: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  createdAt: Timestamp;
  updatedAt: Timestamp;
  moderatorId?: string;
}

export interface InterviewFormData {
  userId: string;
  userName: string;
  userEmail: string;
  companyName: string;
  position: string;
  interviewDate: Date;
  interviewTime: string;
  interviewLocation?: string;
  interviewType: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactRole?: string;
  notes?: string;
}

class InterviewService {
  private collectionName = 'interviews';

  // Ottieni tutte le interviste per il moderatore
  async getAllInterviews(): Promise<Interview[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        orderBy('interviewDate', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Interview[];
    } catch (error) {
      console.error('Error fetching interviews:', error);
      throw new Error('Errore nel caricamento delle interviste');
    }
  }

  // Ottieni interviste per un utente specifico
  async getInterviewsByUser(userId: string): Promise<Interview[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        orderBy('interviewDate', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Interview[];
    } catch (error) {
      console.error('Error fetching user interviews:', error);
      throw new Error('Errore nel caricamento delle interviste utente');
    }
  }

  // Ottieni interviste per oggi
  async getTodayInterviews(): Promise<Interview[]> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const q = query(
        collection(db, this.collectionName),
        where('interviewDate', '>=', Timestamp.fromDate(today)),
        where('interviewDate', '<', Timestamp.fromDate(tomorrow)),
        where('status', '==', 'scheduled'),
        orderBy('interviewTime', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Interview[];
    } catch (error) {
      console.error('Error fetching today interviews:', error);
      throw new Error('Errore nel caricamento delle interviste di oggi');
    }
  }

  // Ottieni interviste per questa settimana
  async getWeekInterviews(): Promise<Interview[]> {
    try {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 7);

      const q = query(
        collection(db, this.collectionName),
        where('interviewDate', '>=', Timestamp.fromDate(startOfWeek)),
        where('interviewDate', '<', Timestamp.fromDate(endOfWeek)),
        orderBy('interviewDate', 'asc'),
        orderBy('interviewTime', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Interview[];
    } catch (error) {
      console.error('Error fetching week interviews:', error);
      throw new Error('Errore nel caricamento delle interviste della settimana');
    }
  }

  // Crea una nuova intervista
  async createInterview(data: InterviewFormData, moderatorId: string): Promise<string> {
    try {
      const interviewData = {
        ...data,
        interviewDate: Timestamp.fromDate(data.interviewDate),
        status: 'scheduled' as const,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        moderatorId
      };

      const docRef = await addDoc(collection(db, this.collectionName), interviewData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating interview:', error);
      throw new Error('Errore nella creazione dell\'intervista');
    }
  }

  // Aggiorna un'intervista
  async updateInterview(interviewId: string, data: Partial<InterviewFormData>): Promise<void> {
    try {
      const updateData: any = {
        ...data,
        updatedAt: Timestamp.now()
      };

      if (data.interviewDate) {
        updateData.interviewDate = Timestamp.fromDate(data.interviewDate);
      }

      await updateDoc(doc(db, this.collectionName, interviewId), updateData);
    } catch (error) {
      console.error('Error updating interview:', error);
      throw new Error('Errore nell\'aggiornamento dell\'intervista');
    }
  }

  // Aggiorna lo status di un'intervista
  async updateInterviewStatus(interviewId: string, status: Interview['status']): Promise<void> {
    try {
      await updateDoc(doc(db, this.collectionName, interviewId), {
        status,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating interview status:', error);
      throw new Error('Errore nell\'aggiornamento dello status dell\'intervista');
    }
  }

  // Elimina un'intervista
  async deleteInterview(interviewId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.collectionName, interviewId));
    } catch (error) {
      console.error('Error deleting interview:', error);
      throw new Error('Errore nell\'eliminazione dell\'intervista');
    }
  }

  // Ottieni statistiche interviste
  async getInterviewStats(): Promise<{
    total: number;
    scheduled: number;
    completed: number;
    cancelled: number;
    today: number;
    thisWeek: number;
  }> {
    try {
      const allInterviews = await this.getAllInterviews();
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 7);

      const todayInterviews = allInterviews.filter(interview => {
        const interviewDate = interview.interviewDate.toDate();
        return interviewDate >= today && interviewDate < tomorrow;
      });

      const weekInterviews = allInterviews.filter(interview => {
        const interviewDate = interview.interviewDate.toDate();
        return interviewDate >= startOfWeek && interviewDate < endOfWeek;
      });

      return {
        total: allInterviews.length,
        scheduled: allInterviews.filter(i => i.status === 'scheduled').length,
        completed: allInterviews.filter(i => i.status === 'completed').length,
        cancelled: allInterviews.filter(i => i.status === 'cancelled').length,
        today: todayInterviews.length,
        thisWeek: weekInterviews.length
      };
    } catch (error) {
      console.error('Error getting interview stats:', error);
      throw new Error('Errore nel calcolo delle statistiche interviste');
    }
  }

  // Cerca interviste
  async searchInterviews(searchTerm: string): Promise<Interview[]> {
    try {
      const allInterviews = await this.getAllInterviews();
      
      return allInterviews.filter(interview => 
        interview.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interview.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interview.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interview.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } catch (error) {
      console.error('Error searching interviews:', error);
      throw new Error('Errore nella ricerca delle interviste');
    }
  }

  // Ottieni colloqui attivi per una candidatura specifica
  async getActiveInterviewsForApplication(userId: string, companyName: string, position: string): Promise<Interview[]> {
    try {
      const allInterviews: Interview[] = [];

      // 1. Cerca nella collection interviews (colloqui moderatore)
      let q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('companyName', '==', companyName),
        where('position', '==', position),
        where('status', '==', 'scheduled'),
        orderBy('interviewDate', 'desc')
      );
      let querySnapshot = await getDocs(q);
      let moderatorInterviews = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Interview[];

      // Se non trovo nulla, riprova solo con userId e companyName
      if (moderatorInterviews.length === 0) {
        q = query(
          collection(db, this.collectionName),
          where('userId', '==', userId),
          where('companyName', '==', companyName),
          where('status', '==', 'scheduled'),
          orderBy('interviewDate', 'desc')
        );
        querySnapshot = await getDocs(q);
        moderatorInterviews = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Interview[];
      }

      // Normalizza i valori lato JS per i colloqui moderatore
      const norm = (v?: string) => (v || '').trim().toLowerCase();
      const normCompany = norm(companyName);
      const normPosition = norm(position);
      
      moderatorInterviews = moderatorInterviews.filter(i => {
        const matchCompany = norm(i.companyName) === normCompany;
        if (normPosition && i.position) {
          return matchCompany && norm(i.position) === normPosition;
        }
        return matchCompany;
      });

      allInterviews.push(...moderatorInterviews);

      // 2. Cerca nei subarray interviews delle candidature (colloqui utente)
      const userInterviews = await this.getUserInterviewsFromApplications(userId, companyName, position);
      allInterviews.push(...userInterviews);

      // Ordina tutti i colloqui per data (più recenti prima)
      return allInterviews.sort((a, b) => {
        const dateA = a.interviewDate?.toMillis ? a.interviewDate.toMillis() : 0;
        const dateB = b.interviewDate?.toMillis ? b.interviewDate.toMillis() : 0;
        return dateB - dateA;
      });

    } catch (error) {
      console.error('Error fetching active interviews for application:', error);
      return [];
    }
  }

  // Ottieni colloqui dai subarray delle candidature
  private async getUserInterviewsFromApplications(userId: string, companyName: string, position: string): Promise<Interview[]> {
    try {
      const userInterviews: Interview[] = [];
      
      // Cerca nelle candidature dell'utente
      const applicationsRef = collection(db, 'applications');
      const applicationsQuery = query(
        applicationsRef,
        where('userId', '==', userId)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      
      // Se non trova nulla nella collection principale, cerca nelle subcollections
      if (applicationsSnapshot.empty) {
        const userApplicationsRef = collection(db, 'users', userId, 'applications');
        const userApplicationsSnapshot = await getDocs(userApplicationsRef);
        
        userApplicationsSnapshot.forEach(doc => {
          const appData = doc.data();
          if (appData.interviews && Array.isArray(appData.interviews)) {
            this.extractInterviewsFromApplication(appData, companyName, position, userInterviews);
          }
        });
      } else {
        applicationsSnapshot.forEach(doc => {
          const appData = doc.data();
          if (appData.interviews && Array.isArray(appData.interviews)) {
            this.extractInterviewsFromApplication(appData, companyName, position, userInterviews);
          }
        });
      }

      return userInterviews;
    } catch (error) {
      console.error('Error fetching user interviews from applications:', error);
      return [];
    }
  }

  // Estrae i colloqui da una singola candidatura
  private extractInterviewsFromApplication(appData: any, companyName: string, position: string, userInterviews: Interview[]): void {
    const norm = (v?: string) => (v || '').trim().toLowerCase();
    const normCompany = norm(companyName);
    const normPosition = norm(position);
    const normAppCompany = norm(appData.companyName);
    const normAppPosition = norm(appData.position);

    // Verifica se la candidatura corrisponde
    const matchCompany = normAppCompany === normCompany;
    const matchPosition = !normPosition || !normAppPosition || normAppPosition === normPosition;
    
    if (matchCompany && matchPosition) {
      appData.interviews.forEach((interview: any) => {
        // Converti il formato colloquio utente nel formato Interview
        const convertedInterview: Interview = {
          id: interview.id,
          userId: appData.userId,
          userName: appData.userName || '',
          userEmail: appData.userEmail || '',
          companyName: appData.companyName,
          position: appData.position,
          interviewDate: interview.date, // Il colloquio utente usa 'date' invece di 'interviewDate'
          interviewTime: this.extractTimeFromDate(interview.date),
          interviewLocation: interview.location || '',
          interviewType: this.convertInterviewType(interview.type),
          contactPerson: interview.contactPerson || '',
          contactEmail: interview.contactEmail || '',
          contactPhone: interview.contactPhone || '',
          contactRole: interview.contactRole || '',
          notes: interview.notes || '',
          status: 'scheduled', // I colloqui utente sono sempre 'scheduled'
          createdAt: interview.date || Timestamp.now(),
          updatedAt: interview.date || Timestamp.now()
        };
        userInterviews.push(convertedInterview);
      });
    }
  }

  // Estrae l'ora da un Timestamp
  private extractTimeFromDate(date: any): string {
    if (!date) return '';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return dateObj.toTimeString().slice(0, 5); // HH:MM
    } catch {
      return '';
    }
  }

  // Converte il tipo di colloquio dal formato utente al formato moderatore
  private convertInterviewType(userType: string): string {
    switch (userType) {
      case 'Presenza': return 'in-person';
      case 'Online': return 'online';
      case 'Telefono': return 'phone';
      default: return 'in-person';
    }
  }
}

export const interviewService = new InterviewService(); 